import classNames from 'classnames';
import omit from 'lodash.omit';
import PropTypes from 'prop-types';
import React from 'react';
import {defineMessages, FormattedMessage, injectIntl, intlShape} from 'react-intl';
import {connect} from 'react-redux';
import MediaQuery from 'react-responsive';
import {Tab, Tabs, TabList, TabPanel} from 'react-tabs';
import tabStyles from 'react-tabs/style/react-tabs.css';
import VM from 'openblock-vm';
import Renderer from 'scratch-render';

import Blocks from '../../containers/blocks.jsx';
import CostumeTab from '../../containers/costume-tab.jsx';
import TargetPane from '../../containers/target-pane.jsx';
import SoundTab from '../../containers/sound-tab.jsx';
import StageWrapper from '../../containers/stage-wrapper.jsx';
import Loader from '../loader/loader.jsx';
import Box from '../box/box.jsx';
import MenuBar from '../menu-bar/menu-bar.jsx';
import CostumeLibrary from '../../containers/costume-library.jsx';
import BackdropLibrary from '../../containers/backdrop-library.jsx';
import Watermark from '../../containers/watermark.jsx';
import Hardware from '../../containers/hardware.jsx';
import HardwareHeader from '../../containers/hardware-header.jsx';
import LibrariesBoardsTab from '../libraries-boards-tab/libraries-boards-tab.jsx';
import BoardManagerTab from '../board-manager-tab/board-manager-tab.jsx';
import PythonCompilerTab from '../python-compiler-tab/python-compiler-tab.jsx';

// eslint-disable-next-line no-unused-vars
import Backpack from '../../containers/backpack.jsx';
import WebGlModal from '../../containers/webgl-modal.jsx';
import TipsLibrary from '../../containers/tips-library.jsx';
import Cards from '../../containers/cards.jsx';
import Alerts from '../../containers/alerts.jsx';
import DragLayer from '../../containers/drag-layer.jsx';
import ConnectionModal from '../../containers/connection-modal.jsx';
import UploadProgress from '../../containers/upload-progress.jsx';
import TelemetryModal from '../telemetry-modal/telemetry-modal.jsx';
import UpdateModal from '../../containers/update-modal.jsx';
import FeedbackModal from '../feedback-modal/feedback-modal.jsx';
import TierModal from '../tier-modal/tier-modal.jsx';
import MobileOverlay from '../mobile-overlay/mobile-overlay.jsx';
import Tour from '../tour/tour.jsx';

import layout, {STAGE_SIZE_MODES} from '../../lib/layout-constants';
import {resolveStageSize} from '../../lib/screen-utils';

import styles from './gui.css';
import addExtensionIcon from './icon--extensions.svg';
import librariesIcon from './icon--libraries.svg';
import codeIcon from './icon--code.svg';
import boardsIcon from './icon--boards.svg';
import costumesIcon from './icon--costumes.svg';
import soundsIcon from './icon--sounds.svg';
import pythonIcon from '../library-item/program-language-python.svg';
import deviceIcon from '../menu-bar/icon--device.svg';

const messages = defineMessages({
    addExtension: {
        id: 'gui.gui.addExtension',
        description: 'Button to add an extension in the target pane',
        defaultMessage: 'Add Extension'
    }
});

// Cache this value to only retrieve it once the first time.
// Assume that it doesn't change for a session.
let isRendererSupported = null;

class GUIComponent extends React.Component {
    constructor (props) {
        super(props);
        this.state = {
            runTour: !localStorage.getItem('hasSeenTour'),
            webGlDismissed: false
        };
        this.handleTourFinish = this.handleTourFinish.bind(this);
        this.handleStartTour = this.handleStartTour.bind(this);
    }

    handleTourFinish () {
        localStorage.setItem('hasSeenTour', 'true');
        this.setState({runTour: false});
    }

    handleStartTour () {
        this.setState({runTour: true});
    }

    render () {
        const {
            accountNavOpen,
            activeTabIndex,
            alertsVisible,
            authorId,
            authorThumbnailUrl,
            authorUsername,
            basePath,
            backdropLibraryVisible,
            // eslint-disable-next-line no-unused-vars
            backpackHost,
            // eslint-disable-next-line no-unused-vars
            backpackVisible,
            blocksTabVisible,
            cardsVisible,
            canChangeLanguage,
            canCreateNew,
            canEditTitle,
            canManageFiles,
            canRemix,
            canSave,
            canCreateCopy,
            canShare,
            canUseCloud,
            children,
            connectionModalVisible,
            uploadProgressVisible,
            costumeLibraryVisible,
            costumesTabVisible,
            updateModalVisible,
            feedbackModalVisible,
            enableCommunity,
            intl,
            isCreating,
            isFullScreen,
            isPlayerOnly,
            isRtl,
            isShared,
            isTelemetryEnabled,
            loading,
            logo,
            renderLogin,
            onClickAbout,
            onClickAccountNav,
            onCloseAccountNav,
            onLogOut,
            onOpenRegistration,
            onToggleLoginOpen,
            onAbortUpdate,
            onActivateCodeTab,
            onActivateCostumesTab,
            onActivateSoundsTab,
            onActivateLibrariesTab,
            onActivateBoardsTab,
            onActivatePythonTab,
            onActivateTab,
            onClickLogo,
            onClickCheckUpdate,
            onClickCheckAgentUpdate,
            onClickUpdate,
            onClickClearCache,
            onClickInstallDriver,
            onExtensionButtonClick,
            onOpenDeviceLibrary,
            onCloseDeviceLibrary,
            onOpenExtensionLibrary,
            onCloseExtensionLibrary,
            onProjectTelemetryEvent,
            onRequestCloseBackdropLibrary,
            onRequestCloseCostumeLibrary,
            onRequestCloseTelemetryModal,
            onRequestCloseFeedbackModal,
            onSeeCommunity,
            onShare,
            onShowPrivacyPolicy,
            onStartSelectingFileUpload,
            onShowMessageBox,
            onTelemetryModalCancel,
            onTelemetryModalOptIn,
            onTelemetryModalOptOut,
            showComingSoon,
            soundsTabVisible,
            librariesTabVisible,
            pythonTabVisible,
            stageSizeMode,
            targetIsStage,
            telemetryModalVisible,
            tipsLibraryVisible,
            vm,
            isRealtimeMode,
            isBlocksMinimized,
            onSetBlocksMinimized,
            userTier,
            tierModalVisible,
            onRequestCloseTierModal,
            ...props
        } = this.props;

        const componentProps = omit(props, ['dispatch', 'onShowBlockLimitAlert', 'onRequestCloseBlockLimitModal', 'onSetSupportSwitchMode', 'onSetBaudrate']);

        if (children) {
            return <Box {...componentProps}>{children}</Box>;
        }

        const tabClassNames = {
            tabs: styles.tabs,
            tab: classNames(tabStyles.reactTabsTab, styles.tab),
            tabList: classNames(tabStyles.reactTabsTabList, styles.tabList),
            tabPanel: classNames(tabStyles.reactTabsTabPanel, styles.tabPanel),
            tabPanelSelected: classNames(tabStyles.reactTabsTabPanelSelected, styles.isSelected),
            tabSelected: classNames(tabStyles.reactTabsTabSelected, styles.isSelected)
        };

        if (isRendererSupported === null) {
            try {
                isRendererSupported = Renderer.isSupported();
                if (!isRendererSupported && typeof document !== 'undefined') {
                    const canvas = document.createElement('canvas');
                    const gl = canvas.getContext('webgl') ||
                        canvas.getContext('experimental-webgl') ||
                        canvas.getContext('webgl2');
                    if (gl) {
                        isRendererSupported = true;
                    }
                }
            } catch (e) {
                isRendererSupported = true;
            }
        }

        const isWebGlDismissed = typeof window !== 'undefined' &&
            (sessionStorage.getItem('dismissWebGlModal') === 'true' ||
             localStorage.getItem('dismissWebGlModal') === 'true');

        return (
            <React.Fragment>
                <Tour
                    run={this.state.runTour}
                    onFinish={this.handleTourFinish}
                    onOpenDeviceLibrary={onOpenDeviceLibrary}
                    onCloseDeviceLibrary={onCloseDeviceLibrary}
                    onOpenExtensionLibrary={onOpenExtensionLibrary}
                    onCloseExtensionLibrary={onCloseExtensionLibrary}
                />
                <MediaQuery maxWidth={640}>
                    <MobileOverlay />
                </MediaQuery>
                <MediaQuery minWidth={layout.fullSizeMinWidth}>{isFullSize => {
                    const stageSize = resolveStageSize(stageSizeMode, isFullSize);

                    return isPlayerOnly ? (
                        <StageWrapper
                            isFullScreen={isFullScreen}
                            isRendererSupported={isRendererSupported}
                            isRtl={isRtl}
                            loading={loading}
                            stageSize={STAGE_SIZE_MODES.large}
                            vm={vm}
                        >
                            {alertsVisible ? (
                                <Alerts
                                    vm={vm}
                                    className={styles.alertsContainer}
                                />
                            ) : null}
                        </StageWrapper>
                    ) : (
                        <Box
                            className={styles.pageWrapper}
                            dir={isRtl ? 'rtl' : 'ltr'}
                            {...componentProps}
                        >
                            {telemetryModalVisible ? (
                                <TelemetryModal
                                    isRtl={isRtl}
                                    isTelemetryEnabled={isTelemetryEnabled}
                                    onCancel={onTelemetryModalCancel}
                                    onOptIn={onTelemetryModalOptIn}
                                    onOptOut={onTelemetryModalOptOut}
                                    onRequestClose={onRequestCloseTelemetryModal}
                                    onShowPrivacyPolicy={onShowPrivacyPolicy}
                                />
                            ) : null}
                            {loading ? (
                                <Loader />
                            ) : null}
                            {isCreating ? (
                                <Loader messageId="gui.loader.creating" />
                            ) : null}
                            {(isRendererSupported || isWebGlDismissed || this.state.webGlDismissed) ? null : (
                                <WebGlModal
                                    isRtl={isRtl}
                                    onDismiss={() => {
                                        isRendererSupported = true;
                                        this.setState({webGlDismissed: true});
                                    }}
                                />
                            )}
                            {tipsLibraryVisible ? (
                                <TipsLibrary />
                            ) : null}
                            {cardsVisible ? (
                                <Cards />
                            ) : null}
                            {alertsVisible ? (
                                <Alerts
                                    vm={vm}
                                    className={styles.alertsContainer}
                                />
                            ) : null}
                            {connectionModalVisible ? (
                                <ConnectionModal
                                    vm={vm}
                                />
                            ) : null}
                            {uploadProgressVisible ? (
                                <UploadProgress
                                    vm={vm}
                                />
                            ) : null}
                            {costumeLibraryVisible ? (
                                <CostumeLibrary
                                    vm={vm}
                                    onRequestClose={onRequestCloseCostumeLibrary}
                                />
                            ) : null}
                            {backdropLibraryVisible ? (
                                <BackdropLibrary
                                    vm={vm}
                                    onRequestClose={onRequestCloseBackdropLibrary}
                                />
                            ) : null}
                            {updateModalVisible ? (
                                <UpdateModal
                                    vm={vm}
                                    onAbortUpdate={onAbortUpdate}
                                    onClickUpdate={onClickUpdate}
                                    onShowMessageBox={onShowMessageBox}
                                />
                            ) : null}
                            {feedbackModalVisible ? (
                                <FeedbackModal
                                    onRequestClose={onRequestCloseFeedbackModal}
                                />
                            ) : null}
                            {tierModalVisible ? (
                                <TierModal
                                    userTier={userTier}
                                    onRequestClose={onRequestCloseTierModal}
                                />
                            ) : null}
                            <div id="tour-menu-bar">
                                <MenuBar
                                    onClickTour={this.handleStartTour}
                                    accountNavOpen={accountNavOpen}
                                    authorId={authorId}
                                    authorThumbnailUrl={authorThumbnailUrl}
                                    authorUsername={authorUsername}
                                    canChangeLanguage={canChangeLanguage}
                                    canCreateCopy={canCreateCopy}
                                    canCreateNew={canCreateNew}
                                    canEditTitle={canEditTitle}
                                    canManageFiles={canManageFiles}
                                    canRemix={canRemix}
                                    canSave={canSave}
                                    canShare={canShare}
                                    className={styles.menuBarPosition}
                                    enableCommunity={enableCommunity}
                                    isShared={isShared}
                                    logo={logo}
                                    renderLogin={renderLogin}
                                    showComingSoon={showComingSoon}
                                    onClickAbout={onClickAbout}
                                    onClickAccountNav={onClickAccountNav}
                                    onClickCheckUpdate={onClickCheckUpdate}
                                    onClickCheckAgentUpdate={onClickCheckAgentUpdate}
                                    onClickClearCache={onClickClearCache}
                                    onClickInstallDriver={onClickInstallDriver}
                                    onClickLogo={onClickLogo}
                                    onCloseAccountNav={onCloseAccountNav}
                                    onLogOut={onLogOut}
                                    onOpenRegistration={onOpenRegistration}
                                    onProjectTelemetryEvent={onProjectTelemetryEvent}
                                    onSeeCommunity={onSeeCommunity}
                                    onShare={onShare}
                                    onShowPrivacyPolicy={onShowPrivacyPolicy}
                                    onStartSelectingFileUpload={onStartSelectingFileUpload}
                                    onShowMessageBox={onShowMessageBox}
                                    onToggleLoginOpen={onToggleLoginOpen}
                                    userTier={userTier}
                                />
                            </div>
                            <Box className={styles.bodyWrapper}>
                                <Box className={styles.flexWrapper}>
                                    <Box className={styles.editorWrapper}>
                                        <Tabs
                                            forceRenderTabPanel
                                            className={tabClassNames.tabs}
                                            selectedIndex={activeTabIndex}
                                            selectedTabClassName={tabClassNames.tabSelected}
                                            selectedTabPanelClassName={tabClassNames.tabPanelSelected}
                                            onSelect={onActivateTab}
                                        >
                                            <div id="tour-tabs-target" style={{position: 'absolute', top: 0, left: 0, height: '40px', width: '400px', pointerEvents: 'none'}} />
                                            <TabList className={tabClassNames.tabList}>
                                                <Tab className={tabClassNames.tab}>
                                                    <img
                                                        draggable={false}
                                                        src={codeIcon}
                                                    />
                                                    <FormattedMessage
                                                        defaultMessage="Code"
                                                        description="Button to get to the code panel"
                                                        id="gui.gui.codeTab"
                                                    />
                                                </Tab>
                                                <Tab
                                                    className={tabClassNames.tab}
                                                    onClick={onActivatePythonTab}
                                                >
                                                    <img
                                                        draggable={false}
                                                        src={pythonIcon}
                                                    />
                                                    <FormattedMessage
                                                        defaultMessage="Python"
                                                        description="Button to get to the python panel"
                                                        id="gui.gui.pythonTab"
                                                    />
                                                </Tab>
                                                <Tab
                                                    className={classNames(tabClassNames.tab,
                                                        isRealtimeMode ? styles.showCustomAndSoundTab :
                                                            styles.hideCustomAndSoundTab)}
                                                    onClick={onActivateLibrariesTab}
                                                >
                                                    <img
                                                        draggable={false}
                                                        src={librariesIcon}
                                                    />
                                                    <FormattedMessage
                                                        defaultMessage="Libraries"
                                                        description="Button to get to the libraries and boards panel"
                                                        id="gui.gui.librariesBoardsTab"
                                                    />
                                                </Tab>
                                                <Tab
                                                    className={classNames(tabClassNames.tab,
                                                        isRealtimeMode ? styles.showCustomAndSoundTab :
                                                            styles.hideCustomAndSoundTab)}
                                                    onClick={onActivateBoardsTab}
                                                >
                                                    <img
                                                        draggable={false}
                                                        src={boardsIcon}
                                                    />
                                                    <FormattedMessage
                                                        defaultMessage="Boards Manager"
                                                        description="Button to get to the boards manager panel"
                                                        id="gui.gui.boardsManagerTab"
                                                    />
                                                </Tab>
                                                <Tab
                                                    className={classNames(tabClassNames.tab,
                                                        isRealtimeMode ? styles.hideCustomAndSoundTab :
                                                            styles.showCustomAndSoundTab)}
                                                    onClick={onActivateCostumesTab}
                                                >
                                                    <img
                                                        draggable={false}
                                                        src={costumesIcon}
                                                    />
                                                    {targetIsStage ? (
                                                        <FormattedMessage
                                                            defaultMessage="Backdrops"
                                                            description="Button to get to the backdrops panel"
                                                            id="gui.gui.backdropsTab"
                                                        />
                                                    ) : (
                                                        <FormattedMessage
                                                            defaultMessage="Costumes"
                                                            description="Button to get to the costumes panel"
                                                            id="gui.gui.costumesTab"
                                                        />
                                                    )}
                                                </Tab>
                                                <Tab
                                                    className={classNames(tabClassNames.tab,
                                                        isRealtimeMode ? styles.hideCustomAndSoundTab :
                                                            styles.showCustomAndSoundTab)}
                                                    onClick={onActivateSoundsTab}
                                                >
                                                    <img
                                                        draggable={false}
                                                        src={soundsIcon}
                                                    />
                                                    <FormattedMessage
                                                        defaultMessage="Sounds"
                                                        description="Button to get to the sounds panel"
                                                        id="gui.gui.soundsTab"
                                                    />
                                                </Tab>
                                            </TabList>
                                            <TabPanel className={tabClassNames.tabPanel}>
                                                <Box
                                                    id="tour-blocks-area"
                                                    className={classNames(styles.blocksWrapper, {
                                                        [styles.paletteMinimized]: isBlocksMinimized,
                                                        'palette-minimized': isBlocksMinimized
                                                    })}
                                                >
                                                    <button
                                                        className={classNames(styles.paletteMinimizeButton, {
                                                            [styles.isMinimized]: isBlocksMinimized
                                                        })}
                                                        title={isBlocksMinimized ? 'Expand Blocks' : 'Minimize Blocks'}
                                                        onClick={() => {
                                                            onSetBlocksMinimized(!isBlocksMinimized);
                                                            window.dispatchEvent(new Event('resize'));
                                                            setTimeout(() => window.dispatchEvent(new Event('resize')), 100);
                                                            setTimeout(() => window.dispatchEvent(new Event('resize')), 300);
                                                        }}
                                                    >
                                                        <div className={styles.minimizeIconWrapper}>
                                                            <svg
                                                                width="16"
                                                                height="16"
                                                                viewBox="0 0 24 24"
                                                                fill="none"
                                                                xmlns="http://www.w3.org/2000/svg"
                                                            >
                                                                <path
                                                                    d="M11 19L4 12L11 5"
                                                                    stroke="currentColor"
                                                                    strokeWidth="2.5"
                                                                    strokeLinecap="round"
                                                                    strokeLinejoin="round"
                                                                />
                                                                <path
                                                                    d="M18 19L11 12L18 5"
                                                                    stroke="currentColor"
                                                                    strokeWidth="2.5"
                                                                    strokeLinecap="round"
                                                                    strokeLinejoin="round"
                                                                />
                                                            </svg>
                                                        </div>
                                                    </button>
                                                    <div id="tour-workspace-only" style={{position: 'absolute', top: 0, bottom: 0, right: 0, left: isBlocksMinimized ? '60px' : '315px', pointerEvents: 'none', zIndex: -1}} />
                                                    <div id="tour-palette-only" style={{position: 'absolute', top: 0, left: 0, bottom: 0, width: isBlocksMinimized ? '60px' : '315px', pointerEvents: 'none', zIndex: -1}} />
                                                    <Blocks
                                                        canUseCloud={canUseCloud}
                                                        grow={1}
                                                        isVisible={blocksTabVisible}
                                                        options={{
                                                            media: `${basePath}static/blocks-media/`
                                                        }}
                                                        stageSize={stageSize}
                                                        vm={vm}
                                                        onShowMessageBox={onShowMessageBox}
                                                    />
                                                </Box>
                                                <Box className={styles.extensionButtonContainer}>
                                                    <button
                                                        id="tour-extension-button"
                                                        className={styles.extensionButton}
                                                        title={intl.formatMessage(messages.addExtension)}
                                                        onClick={onExtensionButtonClick}
                                                    >
                                                        <img
                                                            className={styles.extensionButtonIcon}
                                                            draggable={false}
                                                            src={addExtensionIcon}
                                                        />
                                                    </button>
                                                </Box>
                                                <Box className={styles.watermark}>
                                                    <Watermark />
                                                </Box>
                                            </TabPanel>
                                            <TabPanel className={tabClassNames.tabPanel}>
                                                {pythonTabVisible ? <PythonCompilerTab /> : null}
                                            </TabPanel>
                                            <TabPanel className={tabClassNames.tabPanel}>
                                                {librariesTabVisible ? (
                                                    <LibrariesBoardsTab
                                                        onExtensionButtonClick={onExtensionButtonClick}
                                                        onOpenDeviceLibrary={onOpenDeviceLibrary}
                                                    />
                                                ) : null}
                                            </TabPanel>
                                            <TabPanel className={tabClassNames.tabPanel}>
                                                <BoardManagerTab />
                                            </TabPanel>
                                            <TabPanel className={tabClassNames.tabPanel}>
                                                {costumesTabVisible ? <CostumeTab vm={vm} /> : null}
                                            </TabPanel>
                                            <TabPanel className={tabClassNames.tabPanel}>
                                                {soundsTabVisible ? <SoundTab
                                                    vm={vm}
                                                    onShowMessageBox={onShowMessageBox}
                                                /> : null}
                                            </TabPanel>
                                        </Tabs>
                                    </Box>
                                    <Box
                                        className={classNames(styles.stageAndTargetWrapper, styles[stageSize],
                                            isRealtimeMode ? styles.showStage : styles.hideStage)}
                                    >
                                        <div id="tour-stage-area">
                                            <StageWrapper
                                                isFullScreen={isFullScreen}
                                                isRendererSupported={isRendererSupported}
                                                isRtl={isRtl}
                                                stageSize={stageSize}
                                                vm={vm}
                                            />
                                        </div>
                                        <Box
                                            id="tour-sprite-area"
                                            className={styles.targetWrapper}
                                        >
                                            <TargetPane
                                                stageSize={stageSize}
                                                vm={vm}
                                            />
                                        </Box>
                                    </Box>
                                    {((isRealtimeMode === false) && (stageSizeMode !== STAGE_SIZE_MODES.hide) && activeTabIndex !== 2 && activeTabIndex !== 3 && activeTabIndex !== 1) ? (
                                        <Hardware
                                            vm={vm}
                                            stageSize={stageSize}
                                        />) : null
                                    }
                                </Box>
                                <DragLayer />
                                {(isRealtimeMode === false) ? (
                                    <HardwareHeader
                                        vm={vm}
                                        stageSize={stageSize}
                                    />) : null
                                }
                            </Box>
                        </Box>
                    );
                }}</MediaQuery>
            </React.Fragment>
        );
    }
}

GUIComponent.propTypes = {
    accountNavOpen: PropTypes.bool,
    activeTabIndex: PropTypes.number,
    authorId: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]), // can be false
    authorThumbnailUrl: PropTypes.string,
    authorUsername: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]), // can be false
    backdropLibraryVisible: PropTypes.bool,
    backpackHost: PropTypes.string,
    backpackVisible: PropTypes.bool,
    basePath: PropTypes.string,
    blocksTabVisible: PropTypes.bool,
    canChangeLanguage: PropTypes.bool,
    canCreateCopy: PropTypes.bool,
    canCreateNew: PropTypes.bool,
    canEditTitle: PropTypes.bool,
    canManageFiles: PropTypes.bool,
    canRemix: PropTypes.bool,
    canSave: PropTypes.bool,
    canShare: PropTypes.bool,
    canUseCloud: PropTypes.bool,
    cardsVisible: PropTypes.bool,
    children: PropTypes.node,
    costumeLibraryVisible: PropTypes.bool,
    costumesTabVisible: PropTypes.bool,
    enableCommunity: PropTypes.bool,
    intl: intlShape.isRequired,
    isCreating: PropTypes.bool,
    isFullScreen: PropTypes.bool,
    isPlayerOnly: PropTypes.bool,
    isRtl: PropTypes.bool,
    isShared: PropTypes.bool,
    loading: PropTypes.bool,
    logo: PropTypes.string,
    onActivateCodeTab: PropTypes.func.isRequired,
    onActivateCostumesTab: PropTypes.func,
    onActivateSoundsTab: PropTypes.func,
    onActivateLibrariesTab: PropTypes.func,
    onActivateBoardsTab: PropTypes.func,
    onActivatePythonTab: PropTypes.func,
    onActivateTab: PropTypes.func,
    onClickAccountNav: PropTypes.func,
    onClickLogo: PropTypes.func,
    onClickCheckUpdate: PropTypes.func,
    onClickCheckAgentUpdate: PropTypes.func,
    onAbortUpdate: PropTypes.func,
    onClickUpdate: PropTypes.func,
    onClickClearCache: PropTypes.func,
    onClickInstallDriver: PropTypes.func,
    onCloseAccountNav: PropTypes.func,
    onExtensionButtonClick: PropTypes.func,
    onOpenDeviceLibrary: PropTypes.func,
    onLogOut: PropTypes.func,
    onOpenRegistration: PropTypes.func,
    onRequestCloseBackdropLibrary: PropTypes.func,
    onRequestCloseCostumeLibrary: PropTypes.func,
    onRequestCloseTelemetryModal: PropTypes.func,
    onSeeCommunity: PropTypes.func,
    onShare: PropTypes.func,
    onShowPrivacyPolicy: PropTypes.func,
    onStartSelectingFileUpload: PropTypes.func,
    onShowMessageBox: PropTypes.func.isRequired,
    onTabSelect: PropTypes.func,
    onTelemetryModalCancel: PropTypes.func,
    onTelemetryModalOptIn: PropTypes.func,
    onTelemetryModalOptOut: PropTypes.func,
    onToggleLoginOpen: PropTypes.func,
    onRequestCloseTierModal: PropTypes.func,
    tierModalVisible: PropTypes.bool,
    renderLogin: PropTypes.func,
    showComingSoon: PropTypes.bool,
    soundsTabVisible: PropTypes.bool,
    librariesTabVisible: PropTypes.bool,
    pythonTabVisible: PropTypes.bool,
    stageSizeMode: PropTypes.oneOf(Object.keys(STAGE_SIZE_MODES)),
    targetIsStage: PropTypes.bool,
    telemetryModalVisible: PropTypes.bool,
    tipsLibraryVisible: PropTypes.bool,
    vm: PropTypes.instanceOf(VM).isRequired,
    isRealtimeMode: PropTypes.bool,
    isBlocksMinimized: PropTypes.bool,
    onSetBlocksMinimized: PropTypes.func,
    userTier: PropTypes.string
};

GUIComponent.defaultProps = {
    backpackHost: null,
    backpackVisible: false,
    basePath: './',
    canChangeLanguage: true,
    canCreateNew: false,
    canEditTitle: false,
    canManageFiles: true,
    canRemix: false,
    canSave: false,
    canCreateCopy: false,
    canShare: false,
    canUseCloud: false,
    enableCommunity: false,
    isCreating: false,
    isShared: false,
    loading: false,
    showComingSoon: false,
    stageSizeMode: STAGE_SIZE_MODES.large
};

const mapStateToProps = state => ({
    // This is the button's mode, as opposed to the actual current state
    stageSizeMode: state.scratchGui.stageSize.stageSize
});

export default injectIntl(connect(
    mapStateToProps
)(GUIComponent));
